/**
 * The platform console's "Copy setup from another org" sheet (POST /v1/admin/orgs/{org_id}/copy-setup): from an org's
 * menu, a platform admin chooses the org to copy from, reads what a copy takes and what it leaves, types the target's
 * name in the page (never window.confirm, which some views block), and copies. What the copy wrote comes back as plain
 * lines, then what it skipped, then the next step. Only a platform admin sees any of it: the console redirects
 * everyone else, and the sheet renders nothing for them besides; the route is RequirePlatformAdmin's.
 */
import { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import type { OrgCopySetupSummary, OrgSummary } from "@/lib/api";
import { useOrg } from "@/lib/org-context";
import { Button } from "@/lib/ui/button";
import { Input } from "@/lib/ui/input";
import { NativeSelect } from "@/lib/ui/native-select";
import { Sheet, SheetHeader } from "@/lib/ui/sheet";
import {
  COPY_SETUP_COPIED,
  COPY_SETUP_NOT_COPIED,
  copyNameMatches,
  copySetupReport,
  copySourceOptions,
  runCopySetup,
  type CopySourceOption,
} from "./copy-setup";

export interface CopySetupViewProps {
  targetName: string;
  /** The orgs to copy from, in the order the select lists them. */
  sources: CopySourceOption[];
  /** The chosen source's id, "" before one is chosen. */
  sourceId: string;
  onSourceChange: (id: string) => void;
  /** What has been typed to confirm. */
  typed: string;
  onTypedChange: (typed: string) => void;
  onCopy: () => void;
  copying: boolean;
  /** What the copy wrote, once it is made. */
  result: OrgCopySetupSummary | null;
  /** The last refusal or failure, in words. */
  error: string | null;
  onClose: () => void;
}

const SECTION_HEADING = "text-xs font-semibold uppercase tracking-wide text-on-surface-muted";

/** The sheet's body, pure: exported for the tests. */
export function CopySetupView(p: CopySetupViewProps) {
  if (p.result) {
    const report = copySetupReport(p.result, p.targetName);
    return (
      <div className="flex flex-col gap-5 p-5">
        <section role="status" aria-label="What was copied" className="flex flex-col gap-2">
          <h3 className={SECTION_HEADING}>Copied into {p.targetName}</h3>
          <ul className="flex flex-col gap-1 text-sm text-on-surface">
            {report.lines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </section>
        {report.skipped.length > 0 && (
          <section aria-label="Left out" className="flex flex-col gap-2">
            <h3 className={SECTION_HEADING}>Left out</h3>
            <ul className="flex flex-col gap-1 text-sm text-on-surface">
              {report.skipped.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </section>
        )}
        <p className="rounded-md bg-success/10 px-3 py-2 text-sm text-on-surface">{report.next}</p>
        <div className="flex justify-end">
          <Button type="button" variant="ghost" size="sm" onClick={p.onClose}>
            Close
          </Button>
        </div>
      </div>
    );
  }

  const chosen = p.sources.some((s) => s.id === p.sourceId);
  const canCopy = chosen && copyNameMatches(p.typed, p.targetName) && !p.copying;
  return (
    <div className="flex flex-col gap-5 p-5">
      <p className="text-sm text-on-surface-muted">
        Copy another org&apos;s setup into {p.targetName}, so the plant&apos;s own files can be imported into it again.
      </p>

      <section className="flex flex-col gap-2" aria-label="What is copied">
        <h3 className={SECTION_HEADING}>Copied</h3>
        <ul className="list-disc pl-5 text-sm text-on-surface">
          {COPY_SETUP_COPIED.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </section>

      <section className="flex flex-col gap-2" aria-label="What is not copied">
        <h3 className={SECTION_HEADING}>Not copied</h3>
        <ul className="list-disc pl-5 text-sm text-on-surface">
          {COPY_SETUP_NOT_COPIED.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </section>

      <form
        className="flex flex-col gap-3"
        aria-label="Copy setup"
        onSubmit={(e) => {
          e.preventDefault();
          if (canCopy) p.onCopy();
        }}
      >
        <div className="flex flex-col gap-1 text-sm">
          <label htmlFor="copy-setup-source" className="font-medium text-on-surface">
            Copy from
          </label>
          {p.sources.length === 0 ? (
            <p className="text-sm text-on-surface-muted">There is no other org to copy from.</p>
          ) : (
            <NativeSelect
              id="copy-setup-source"
              name="source_org_id"
              value={p.sourceId}
              onChange={(e) => p.onSourceChange(e.target.value)}
              disabled={p.copying}
            >
              <option value="">Choose the org to copy from</option>
              {p.sources.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </NativeSelect>
          )}
        </div>
        <div className="flex flex-col gap-1 text-sm">
          <label htmlFor="copy-setup-confirm" className="font-medium text-on-surface">
            {`Type the org's name, ${p.targetName}, to confirm`}
          </label>
          <Input
            id="copy-setup-confirm"
            name="confirm"
            autoComplete="off"
            value={p.typed}
            placeholder={p.targetName}
            onChange={(e) => p.onTypedChange(e.target.value)}
            disabled={p.copying}
          />
        </div>
        <div className="flex items-center justify-end gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={p.onClose} disabled={p.copying}>
            Close
          </Button>
          <Button type="submit" variant="primary" size="sm" disabled={!canCopy}>
            {p.copying && <Loader2 className="mr-1.5 size-4 animate-spin" />}
            Copy setup
          </Button>
        </div>
      </form>

      {p.error && (
        <p role="alert" className="text-sm text-danger">
          {p.error}
        </p>
      )}
    </div>
  );
}

/** The sheet's state and its call: the source and the typed name, then the copy, then what it wrote. */
function CopySetupEditor({
  target,
  orgs,
  onClose,
  onCopied,
}: {
  target: Pick<OrgSummary, "id" | "name">;
  orgs: readonly Pick<OrgSummary, "id" | "name" | "slug">[];
  onClose: () => void;
  onCopied?: () => void;
}) {
  const sources = useMemo(() => copySourceOptions(orgs, target.id), [orgs, target.id]);
  const [sourceId, setSourceId] = useState("");
  const [typed, setTyped] = useState("");
  const [copying, setCopying] = useState(false);
  const [result, setResult] = useState<OrgCopySetupSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const copy = async () => {
    if (copying || !sources.some((s) => s.id === sourceId) || !copyNameMatches(typed, target.name)) return;
    setError(null);
    setCopying(true);
    const made = await runCopySetup(target, sourceId);
    setCopying(false);
    if ("error" in made) {
      setError(made.error);
      return;
    }
    setResult(made.summary);
    onCopied?.();
  };

  return (
    <CopySetupView
      targetName={target.name}
      sources={sources}
      sourceId={sourceId}
      onSourceChange={(id) => {
        setSourceId(id);
        setError(null);
      }}
      typed={typed}
      onTypedChange={(v) => {
        setTyped(v);
        setError(null);
      }}
      onCopy={() => void copy()}
      copying={copying}
      result={result}
      error={error}
      onClose={onClose}
    />
  );
}

/**
 * The sheet, for the org that is to take the copy; `orgs` is every org, from which the sources are listed. The parent
 * keys it by org. `onCopied` runs once a copy is made, so the console can read its orgs again. Nothing for anyone but a
 * platform admin.
 */
export function CopySetupSheet({
  target,
  orgs,
  onClose,
  onCopied,
}: {
  target: Pick<OrgSummary, "id" | "name"> | null;
  orgs: readonly Pick<OrgSummary, "id" | "name" | "slug">[];
  onClose: () => void;
  onCopied?: () => void;
}) {
  const { isPlatformAdmin } = useOrg();
  if (!target || !isPlatformAdmin) return null;
  return (
    <Sheet open onClose={onClose} ariaLabel={`Copy setup into ${target.name}`} widthClass="max-w-lg">
      <SheetHeader title="Copy setup" subtitle={target.name} onClose={onClose} />
      <CopySetupEditor target={target} orgs={orgs} onClose={onClose} onCopied={onCopied} />
    </Sheet>
  );
}
